import React from "react";
import { Row, Col, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import type { SettingKey } from "./HierarchyManagementConstants";
import TimezoneSelect from "../components/TimezoneSelect";
import type { WizardData } from "./HierarchyManagement";

interface SettingsFormProps {
  settings: readonly SettingKey[];
  wizardData: WizardData;
  setWizardData: React.Dispatch<React.SetStateAction<WizardData>>;
  override: Record<string, boolean>;
  setOverride: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  prefix?: "location" | "nursery";
  parentData?: Record<string, any>;
  showOverride?: boolean;
}

const SettingsForm: React.FC<SettingsFormProps> = ({
  settings,
  wizardData,
  setWizardData,
  override,
  setOverride,
  prefix = "",
  parentData = {},
  showOverride = true,
}) => {
  const { t } = useTranslation('pages', { keyPrefix: 'settingsForm' });

  const getValue = (s: SettingKey) => {
    if (prefix && override[s]) {
      return wizardData[`${prefix}_${s}` as keyof WizardData];
    }
    return parentData[s] ?? wizardData[s as keyof WizardData];
  };

  const handleChange = (s: SettingKey, value: any) => {
    const key = prefix ? `${prefix}_${s}` : s;
    setWizardData((d: WizardData) => ({ ...d, [key]: value }));
  };

  const handleOverrideChange = (s: SettingKey, checked: boolean) => {
    setOverride((o) => ({ ...o, [s]: checked }));
    if (!checked && prefix) {
      setWizardData((d: WizardData) => ({
        ...d,
        [`${prefix}_${s}`]: parentData[s] ?? d[s as keyof typeof d],
      }));
    }
  };

  return (
    <Row className="g-3">
      {settings.map((s) => (
        <Col md={6} key={s}>
          <Form.Group>
            <Form.Label className="small fw-bold text-muted text-uppercase mb-1">
              {t(s)}{" "}
              {!override[s] && prefix && (
                <span
                  className="text-info lowercase"
                  style={{ fontSize: "0.7rem" }}
                >
                  {t('inherited')}
                </span>
              )}
            </Form.Label>
            <div className="d-flex align-items-center">
              {s === "timezone" ? (
                <div className="flex-grow-1">
                  <TimezoneSelect
                    value={getValue(s) as string || ""}
                    onChange={(v) => handleChange(s, v)}
                    disabled={prefix ? !override[s] : false}
                  />
                </div>
              ) : (
                <Form.Control
                  type={
                    s.includes("_th") ||
                    s.includes("_from") ||
                    s.includes("_to") ||
                    s === "similarity_addition" ||
                    s.includes("confidence")
                      ? "number"
                      : "text"
                  }
                  step={
                    s.includes("_th") ||
                    s.includes("_from") ||
                    s.includes("_to") ||
                    s === "similarity_addition" ||
                    s.includes("confidence")
                      ? "0.01"
                      : undefined
                  }
                  min={s.includes("confidence") ? 0 : undefined}
                  max={s.includes("confidence") ? 1 : undefined}
                  value={getValue(s) ?? ""}
                  disabled={prefix ? !override[s] : false}
                  onChange={(e) => {
                    const isNumeric =
                      s.includes("_th") ||
                      s.includes("_from") ||
                      s.includes("_to") ||
                      s === "similarity_addition" ||
                      s.includes("confidence");
                    const val = isNumeric
                      ? e.target.value === ""
                        ? undefined
                        : Number(e.target.value)
                      : e.target.value;
                    handleChange(s, val);
                  }}
                />
              )}
              {showOverride && prefix && (
                <Form.Check
                  className="ms-2"
                  type="switch"
                  label={<span className="small text-muted">{t('override')}</span>}
                  checked={override[s] || false}
                  onChange={(e) => handleOverrideChange(s, e.target.checked)}
                />
              )}
            </div>
          </Form.Group>
        </Col>
      ))}
    </Row>
  );
};

export default SettingsForm;
